import collaborationStore from '@/store/collaboration.store';
import { IModifyRoleParams, IOriginalData, IPG } from '@/types/summary';
import { throttle } from 'lodash';
import { isEqual } from 'lodash';
import { v4 as uuidV4 } from 'uuid';
import {
  setNewSpeakerAvatar,
} from "@/utils/newAvatar";
import { createNewSent, getApiDataFromUIData, modifyExistedSent } from './utils';
import { IEditRecordParams, IMergeRecordParams } from '@/types/record';

const PUCUATIONS_REG = /[？，。！、；：?,.;:!]/g;
const GAP_TIME = 300000;
const MAX_TEXT_PER_RECORD = 150;
// const MAX_TRANSLATE_TEXT_PER_RECORD = 300;

class AsrHandler {
  constructor() {
    throw new Error('AsrHandler is a static class');
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public static handleAsr(data: any, handlerSpeaker = false) {
    const { texts, status, originalData } = data;
    switch (status) {
      case 'unstable':
        this._handleUnstableTextThrottle(texts);
        break;
      case 'stable':
        if (handlerSpeaker) return;
        this._handleCompleteText(texts);
        break;
      case 'completeWithSpeakerName':
        if (!handlerSpeaker) return;
        this._handleCompleteText(texts, originalData);
        break;
      case 'transcriptionSupplement':
        if (!handlerSpeaker) return;
        this._handleCompleteText(texts);
        this.initRecentSpeakersFromContent(texts);
        break;
      case 'correct':
      case 'transcriptionUpdateTrans':
        this._handleCorrectText(texts);
        break;
      case 'personalModifyRole':
        this._handlePersonalModifyRole(originalData);
        break;
    }
  }

  private static _handlePersonalModifyRole = (data: IModifyRoleParams) => {
    if (!data) return;
    const { pis, roleName, role, editRoleType } = data;
    if (editRoleType === 'ONE') {
      this.modifySpeakerName(pis[0], [roleName], (updatedParas) => {
        console.log("AsrHandler: _handlePersonalModifyRole", updatedParas);
      });
    } else {
      this.modifyAllSameSpeakerName(pis[0], [role], [roleName], (updatedPara) => {
        console.log("AsrHandler: _handlePersonalModifyRole", updatedPara);
      }, true);
    }
  }

  private static _handleCorrectText = (texts: IPG[]) => {
    texts.forEach((correctItem) => {
      this._handleCorrectRecord(correctItem);
    });
  }

  private static _handleCorrectRecord(correctItem: IPG) {
    const focusedPara = collaborationStore.getState().getFocusedPara();
    const editedPiArr = collaborationStore.getState().getEditedPiArr();


    // 元素没有经过修改，才会进入校正
    if (editedPiArr.includes(correctItem.pi)) {
      console.log(`AsrHandler: 段落 ${correctItem.pi} 已被编辑，跳过校正`);
      return;
    }

    // 元素聚焦，等待失焦后取出校正
    if (correctItem.pi === focusedPara?.pi) {
      console.log(`AsrHandler: 段落 ${correctItem.pi} 正在聚焦，等待失焦后校正`);
      const waitingCorrectRecords = collaborationStore.getState().getWaitingCorrectRecords();
      collaborationStore.getState().setWaitingCorrectRecords([...waitingCorrectRecords, correctItem]);
      return;
    }

    // 元素没有经过修改，且没有聚焦，直接校正
    this._applyCorrectRecord(correctItem);
  }

  private static _applyCorrectRecord(correctItem: IPG) {
    const _srcContent = [...collaborationStore.getState().srcContent];

    for (let index = _srcContent.length - 1; index >= 0; index--) {
      const item = _srcContent[index];
      if (item.pi !== correctItem.pi && !item.sc.some(sItem => sItem.realPara.pi === correctItem.pi)) continue;

      // 创建新的段落对象以确保 React 检测到变化
      const updatedItem = { ...item };
      updatedItem.sc = item.sc
        .map(sItem => {
          // 这里改成用 si 来判断，用 bt 来判断有可能会拿错译文的句子，导致校正了句子
          const targetSent = correctItem.sc.find(correctSItem => correctSItem.si === sItem.si);
          if (targetSent) {
            // 创建新的句子对象
            return {
              ...sItem,
              tc: targetSent.tc,
              transTc: targetSent.transTc
            };
          }
          return sItem;
        })
      // 如果段落中同时有 tc 和 transTc 的句子，且有 ri 的话，则不保留当前句子，不然句子校正后有可能出现两个相同的译文句子
      updatedItem.sc = updatedItem.sc.filter(fItem => {
        if (fItem.ri) {
          const resourceItem = updatedItem.sc.find(sItem => sItem.si === fItem.ri);
          if (resourceItem && resourceItem.tc && resourceItem.transTc) {
            return false;
          }
        }
        return true;
      })

      // 更新数组中的对象
      _srcContent[index] = updatedItem;
      break;
    }

    collaborationStore.getState().setSrcContent([..._srcContent]);
  }

  private static _handleUnstableText = (texts: IPG[]) => {
    const str = texts.length > 0 ? texts[0].sc.map(sItem => sItem.tc).join("") : "";
    const translationStr = texts.length > 0 ? texts[0].sc.map(sItem => sItem.transTc).join("") : "";
    if (!str && !translationStr) return;
    const unstableText = str || collaborationStore.getState().unstableText;
    const unstableTranslateText = translationStr || collaborationStore.getState().unstableTranslateText;
    // 去掉不稳定态文本中的标点符号
    const unstableTextWithoutPunctuation = unstableText.replaceAll(PUCUATIONS_REG, '');
    const unstableTranslateTextWithoutPunctuation = unstableTranslateText.replaceAll(
      PUCUATIONS_REG,
      ''
    );
    collaborationStore.setState({
      unstableText: unstableTextWithoutPunctuation,
      unstableTranslateText: unstableTranslateTextWithoutPunctuation
    });
  };


  private static _handleUnstableTextThrottle = throttle(this._handleUnstableText, 100);

  /**
   * 处理带发言人区分的稳定态文本
   * @param texts
   * @returns
   */
  // private static _handleStableTextWithSpeaker = (texts: IPG[]) => {
  //   const { srcContent } = collaborationStore.getState();
  //   collaborationStore.getState().setSrcContent([...srcContent, ...texts]);
  //   collaborationStore.getState().setUnstableText('');
  // };

  /**
   * 发言人区分完成，最终状态
   * @param data
   */
  private static _handleCompleteText = (texts: IPG[], originalData?: IOriginalData) => {
    const _srcContent = [...collaborationStore.getState().srcContent];
    const focusedPara = collaborationStore.getState().getFocusedPara();
    const newContent: IPG[] = [];
    const lastContent: IPG | undefined = _srcContent[_srcContent.length - 1];
    const _texts = this._mergeText(texts);
    // 处理新增的文本数据
    const processedTexts = _texts.map(item => {
      // 检查是否有 role 映射，如果有就使用映射的发言人名称
      if (item.role) {
        const mappedSpeakerName = collaborationStore.getState().getRoleSpeakerMapping(item.role);
        if (mappedSpeakerName) {
          // 使用映射的发言人名称，但保持其他信息不变
          return {
            ...item,
            speakerName: mappedSpeakerName,
            // 保持原有的头像和其他信息
          };
        }
      }
      return item;
    });

    if (_srcContent.length) {
      processedTexts.forEach((item) => {
        // 如果有重复（断网重连会导致重复）就不添加
        if (_srcContent.findIndex((i) => i.pi === item.pi) !== -1) {
          return;
        }
        // 如果是同一个角色且说话间隔不超过一定时间，就合并
        // 如果不是同一个角色，就添加到 records 中
        const lastContentStr = lastContent.sc.map(item => item.tc).join("");
        const currentSc = item.sc;
        const currentScContent = currentSc.map(item => item.tc).join("");
        if (
          // 用 ri 来判断是否能够用于视觉段落，不能是译文作为视觉段落，所以如果有 ri 直接 push，不进行合并
          !lastContent.ri &&
          // 有选中段落时，不做合并
          focusedPara?.pi !== lastContent.pi &&
          item.role === lastContent.role &&
          item.sc[0].bt - (lastContent.sc[lastContent.sc.length - 1].et || 0) < GAP_TIME &&
          lastContentStr.length + currentScContent.length < MAX_TEXT_PER_RECORD
        ) {
          lastContent.sc = lastContent.sc.concat(currentSc);
          lastContent.et = item.et;
        } else {
          newContent.push(item);
        }
      });
    } else {
      newContent.push(...processedTexts);
    }
    if (lastContent) {
      _srcContent.splice(_srcContent.length - 1, 1);
      collaborationStore.getState().setSrcContent([..._srcContent, lastContent, ...newContent]);
    } else {
      collaborationStore.getState().setSrcContent([..._srcContent, ...newContent]);
    }

    const { stashPg, stashTransPg } = originalData || {};
    // console.log('originalData', stashPg, stashTransPg);

    if (stashPg && stashPg.length > 0) {
      //  判断 stashPg 或者 transPg 是否存在，取得所有的字符串，设置为非稳态
      const stashText = stashPg.map(item => item.sc.map(sItem => sItem.tc).join("")).join("");
      // console.log('stashText', stashText);
      collaborationStore.getState().setUnstableText(stashText || "");
    } else {
      collaborationStore.getState().setUnstableText('');
    }
    if (stashTransPg && stashTransPg.length > 0) {
      const transStashText = stashTransPg.map(item => item.sc.map(sItem => sItem.tc).join("")).join("");
      // console.log('transStashText', transStashText);
      collaborationStore.getState().setUnstableTransText(transStashText || "");
    } else {
      collaborationStore.getState().setUnstableTransText('');
    }
  };

  /**
   * 合并文本
   * @param texts
   */
  private static _mergeText: (texts: IPG[]) => IPG[] = (texts) => {
    const res: IPG[] = [];
    texts.forEach((item, index, arr) => {
      const copyItem = { ...item, sc: [] };
      // realPara 的 sc 不需要维护，从 UI 数据结构中获取即可
      item.sc = item.sc.map(sItem => {
        return {
          ...sItem,
          realPara: copyItem,
        }
      });
      if (index === 0) {
        res.push(item);
      } else {
        const newestSc = arr[index - 1].sc;
        const newestScContent = newestSc.map(item => item.tc).join("");
        const currentSc = item.sc;
        const currentScContent = currentSc.map(item => item.tc).join("");

        const newestScEt = newestSc[newestSc.length - 1].et;
        if (
          item.role === arr[index - 1].role &&
          item.sc[0].bt - newestScEt < GAP_TIME &&
          newestScContent.length + currentScContent.length < MAX_TEXT_PER_RECORD
        ) {
          const lastRes = res[res.length - 1];
          lastRes.sc = lastRes.sc.concat(currentSc);
          lastRes.et = item.et;
        } else {
          res.push(item);
        }
      }
    });
    return res;
  };

  /**
   * 合并记录 - 将指定记录与前一个记录合并
   * @param pi 要合并的段落ID
   * @returns 返回合并后的记录索引，如果失败返回undefined
   */
  public static mergeRecord(pi: string, successCb?: (para: IMergeRecordParams) => void | Promise<void>): number | void {
    const _srcContent = [...collaborationStore.getState().srcContent];
    const recordIndex = _srcContent.findIndex((item) => item.pi === pi);
    const lastRecord = _srcContent[recordIndex - 1];
    const targetRecord = _srcContent[recordIndex];

    if (!targetRecord) return;

    if (!lastRecord) {
      // 如果没有前一个记录，直接删除当前记录
      const filteredContent = _srcContent.filter((item) => item.pi !== pi);
      collaborationStore.getState().setSrcContent(filteredContent);
      successCb?.({
        modifiedParas: [],
        deletedParas: [targetRecord],
      });
      return 0;
    }

    // 获取合并前光标位置
    const targetCursorPos = lastRecord.sc.map(item => item.tc).join("").length;
    const targetSi = lastRecord.sc[lastRecord.sc.length - 1].si;

    // 合并句子内容
    lastRecord.sc = lastRecord.sc.concat(targetRecord.sc);
    lastRecord.et = targetRecord.et || lastRecord.et;

    // 移除被合并的记录
    const filteredContent = _srcContent.filter((item) => item.pi !== pi);
    collaborationStore.getState().setSrcContent(filteredContent);

    successCb?.({
      cursorPos: {
        pi: lastRecord.pi,
        si: targetSi,
        pos: targetCursorPos,
      },
      deletedParas: getApiDataFromUIData(targetRecord),
      modifiedParas: [{
        ...lastRecord, sc: lastRecord.sc.map(sItem => {
          sItem.realPara = { ...lastRecord, sc: [] };
          return sItem;
        })
      }],
    });

    return recordIndex - 1;
  }

  /**
   * 拆分记录 - 在指定位置拆分记录
   * @param pi 段落 ID
   * @param si 句子 ID
   * @param cIndex 字符索引位置
   * @returns 返回拆分后的记录索引
   */
  public static splitRecord(pi: string, si: string, cIndex: number): number | void {
    const _srcContent = [...collaborationStore.getState().srcContent];
    const targetParaIndex = _srcContent.findIndex((item) => item.pi === pi);
    const targetPara = _srcContent[targetParaIndex];

    if (!targetPara) return;

    const targetScIndex = targetPara.sc.findIndex((item) => item.si === si);
    const targetSc = targetPara.sc[targetScIndex];

    if (!targetSc || cIndex <= 0 || cIndex >= targetSc.tc.length) return;

    // 拆分文本内容
    const beforeText = targetSc.tc.substring(0, cIndex);
    const afterText = targetSc.tc.substring(cIndex);

    // 创建新的句子
    const newSc = {
      ...targetSc,
      si: `s-${uuidV4()}`,
      tc: afterText,
      transTc: "", // 拆分后的翻译文本重置
    };

    // 更新原句子
    targetSc.tc = beforeText;

    // 创建新的段落
    const newPara: IPG = {
      ...targetPara,
      pi: `p-${uuidV4()}`,
      sc: [newSc, ...targetPara.sc.slice(targetScIndex + 1)],
    };

    // 更新原段落（只保留拆分点之前的句子）
    targetPara.sc = targetPara.sc.slice(0, targetScIndex + 1);

    // 插入新段落
    _srcContent.splice(targetParaIndex + 1, 0, newPara);
    collaborationStore.getState().setSrcContent(_srcContent);

    return targetParaIndex + 1;
  }

  /**
   * 编辑记录 - 修改指定句子的内容
   * @param pi 段落 ID
   * @param edits 编辑内容数组
   */
  public static editRecord(
    pi: string,
    edits: Array<{
      si: string;
      newContent: string;
    }>,
    successCb?: (params: IEditRecordParams) => void | Promise<void>
  ): void {
    const _srcContent = [...collaborationStore.getState().srcContent];
    const targetPara = _srcContent.find((item) => item.pi === pi);
    if (!targetPara) return;
    // sc 永远不为空，如果为空，则创建一个新句子，所以这里需要先拿到段落的 bt 和 et
    const paraBt = targetPara.sc[0]?.bt;
    const paraEt = targetPara.sc[targetPara.sc.length - 1]?.et;

    let hasEdited = false;
    const resultMap = new Map<string, IPG>();
    edits.forEach(({ si, newContent }) => {
      const targetSc = targetPara.sc.find((item) => item.si === si);
      if (targetSc) {
        // 如果内容没有变化，则不进行修改
        if (targetSc.tc === newContent) return;
        // 只要有一次编辑过，就会永远存在编辑列表中
        !hasEdited && (hasEdited = true);
        // 修改前端视觉
        targetSc.tc = newContent;

        // 修改传递给后台的数据
        const { realPara } = targetSc;
        const realParaScList = targetPara.sc.filter(item => item.realPara.pi === realPara.pi);
        if (!resultMap.has(realPara.pi)) {
          resultMap.set(realPara.pi, {
            ...realPara,
            // 更新头像/发言人信息
            speakerName: targetPara.speakerName,
            headImage: targetPara.headImage,
            useDefaultIcon: targetPara.useDefaultIcon,
            role: targetPara.role,
            // 更新句子内容
            sc: realParaScList?.map(sItem => {
              if (sItem.si === si) {
                return modifyExistedSent(sItem, newContent);
              }
              return sItem;
            }),
          });
        }
      }
      // 有新增句子的
      else if (newContent) {
        const newSent = createNewSent(targetPara, newContent, paraBt, paraEt);
        if (resultMap.has(pi)) {
          targetPara.sc.unshift(newSent);
          resultMap.get(pi)!.sc.unshift(newSent);
        } else {
          targetPara.sc = [newSent];
          resultMap.set(pi, { ...targetPara, sc: [newSent] });
        }
        !hasEdited && (hasEdited = true);
      }
      // 纯删除
      else {
        resultMap.set(pi, {
          ...targetPara,
          sc: [],
        });
        !hasEdited && (hasEdited = true);
      }
    })

    if (!hasEdited) return;

    // 过滤掉空内容的句子
    const newSent = createNewSent(targetPara, "", paraBt, paraEt);
    targetPara.sc = targetPara.sc.filter((item) => item.tc.trim().length > 0 || item.transTc.trim().length > 0 || item.isEdited);
    const hasNonEmptySc = targetPara.sc.some(item => item.tc);
    if (!hasNonEmptySc) {
      targetPara.sc = Array.isArray(targetPara.sc) ? targetPara.sc.concat(newSent) : [newSent];
    }

    const editedParas = Array.from(resultMap.values()).map(item => {
      item.sc = item.sc ? item.sc.filter(item => item.tc.trim().length > 0) : [];
      return item;
    });

    const nonEmptyModifiedParas = editedParas.filter(item => item.sc.length > 0);
    let modifiedParas: IPG[] = [];
    let deletedParas: IPG[] = [];
    // 如果有不为空的段落，则删除空段落
    if (nonEmptyModifiedParas.length > 0) {
      modifiedParas = nonEmptyModifiedParas;
      deletedParas = editedParas.filter(item => item.sc.length === 0);
    }
    // 全部都是空段落，则修改第一个段落，补充空句子，删除其他段落
    else {
      const [firstPara, ...restParas] = editedParas;
      modifiedParas = [{ ...firstPara, sc: [newSent] }];
      deletedParas = restParas;
    }
    successCb?.({
      modifiedParas,
      deletedParas,
    });
    collaborationStore.getState().setSrcContent(_srcContent);
  }

  /**
   * 修改发言人名称
   * @param pi 段落 ID
   * @param newSpeakerName 新的发言人名称
   * @param successCb 成功回调函数，用于处理后台接口调用
   */
  public static modifySpeakerName(
    pi: string,
    newSpeakerName: string[],
    successCb?: (updatedParas: IPG[]) => void | Promise<void>
  ): void {
    const _srcContent = [...collaborationStore.getState().srcContent];
    const targetPara = _srcContent.find((item) => item.pi === pi);

    if (!targetPara) {
      console.warn("AsrHandler.modifySpeakerName: targetPara not found for pi:", pi);
      return;
    }

    // 生成发言人信息，但保持原有的 role
    const { avatar, role } = setNewSpeakerAvatar(newSpeakerName[0]);

    // 创建新的对象以确保引用改变，触发 React 重新渲染
    const updatedPara = {
      ...targetPara,
      speakerName: newSpeakerName,
      headImage: avatar,
      useDefaultIcon: true,
      // 保持原有的 role 不变
      role,
    };

    const apiData = getApiDataFromUIData(updatedPara);
    console.log('modifySpeakerName updatedPara', updatedPara, apiData);
    successCb?.(apiData);

    // 创建新的数组并替换目标段落
    const newSrcContent = _srcContent.map(item =>
      item.pi === pi ? updatedPara : item
    );

    // 添加发言人到最近列表
    collaborationStore.getState().unshiftRecentSpeakers([
      {
        speakerName: newSpeakerName,
        headImage: avatar,
        useDefaultIcon: true,
        role: role || "",
      },
    ]);

    collaborationStore.getState().setSrcContent(newSrcContent);
  }

  /**
   * 批量修改相同发言人名称
   * @param oldSpeakerName 旧的发言人名称
   * @param newSpeakerName 新的发言人名称
   * @param successCb 成功回调函数，用于处理后台接口调用
   */
  public static modifyAllSameSpeakerName(
    pi: string,
    oldSpeakerNameOrRole: string[],
    newSpeakerName: string[],
    successCb?: (updatedPara: IPG) => void | Promise<void>,
    isModifyByRole = false
  ): void {
    console.log("modifyAllSameSpeakerName", oldSpeakerNameOrRole, newSpeakerName);

    const _srcContent = [...collaborationStore.getState().srcContent];
    const targetPara = _srcContent.find((item) => item.pi === pi);

    if (!targetPara) {
      console.warn("AsrHandler.modifyAllSameSpeakerName: targetPara not found for pi:", pi);
      return;
    }
    const { avatar } = setNewSpeakerAvatar(newSpeakerName[0]);

    // 收集所有需要修改的 role，建立映射关系
    const rolesToUpdate = new Set<string>();

    const isEqualFn = (item: IPG) => isModifyByRole ? isEqual(item.role, oldSpeakerNameOrRole[0]) : isEqual(item.speakerName, oldSpeakerNameOrRole);

    // 使用不可变更新，创建新的数组和对象
    const newSrcContent = _srcContent.map((item) => {
      if (isEqualFn(item)) {
        // 收集这个发言人的 role
        if (item.role) {
          rolesToUpdate.add(item.role);
        }

        return {
          ...item,
          speakerName: newSpeakerName,
          headImage: avatar,
          useDefaultIcon: true,
          // 保持原有的 role 不变
          role: item.role,
        };
      }
      return item;
    });

    successCb?.({
      ...targetPara,
      speakerName: newSpeakerName,
    });

    // 为所有相关的 role 建立映射关系，确保后续新增的同 role 发言人使用新名称
    rolesToUpdate.forEach(role => {
      collaborationStore.getState().setRoleSpeakerMapping(role, newSpeakerName);
    });

    if (rolesToUpdate.size > 0) {
      console.log("已建立的角色映射：", this.getRoleSpeakerMappings());
    }

    // 添加发言人到最近列表（使用第一个匹配项的 role）
    const firstMatchItem = newSrcContent.find(isEqualFn);
    if (firstMatchItem) {
      collaborationStore.getState().unshiftRecentSpeakers([
        {
          speakerName: newSpeakerName,
          headImage: avatar,
          useDefaultIcon: true,
          role: firstMatchItem.role || "",
        },
      ]);
    }

    collaborationStore.getState().setSrcContent(newSrcContent);
  }

  /**
   * 清除特定角色的发言人映射
   * @param role 角色 ID
   */
  public static clearRoleSpeakerMapping(role: string): void {
    collaborationStore.getState().removeRoleSpeakerMapping(role);
    console.log(`已清除角色 ${role} 的映射关系`);
  }

  /**
   * 清除所有角色映射
   */
  public static clearAllRoleSpeakerMappings(): void {
    collaborationStore.getState().clearRoleSpeakerMapping();
    console.log("已清除所有角色映射关系");
  }

  /**
   * 获取当前的角色映射状态
   */
  public static getRoleSpeakerMappings(): Record<string, string[]> {
    return collaborationStore.getState().roleSpeakerMap;
  }

  /**
   * 标记记录
   * @param pi 段落 ID
   * @param signFlag 标记状态
   */
  public static signRecord(pi: string, signFlag = true): void {
    const _srcContent = [...collaborationStore.getState().srcContent];
    const targetPara = _srcContent.find((item) => item.pi === pi);

    if (!targetPara) return;

    targetPara.sc.forEach((sItem) => {
      sItem.mark = signFlag;
    });

    collaborationStore.getState().setSrcContent(_srcContent);
  }


  /**
   * 从现有内容初始化最近发言人列表
   * @param srcContent 源内容数组
   */
  public static initRecentSpeakersFromContent(srcContent?: IPG[]): void {
    const content = srcContent || collaborationStore.getState().srcContent;

    content.forEach((item) => {
      if (item.speakerName && item.speakerName.length > 0) {
        collaborationStore.getState().unshiftRecentSpeakers([
          {
            speakerName: item.speakerName,
            headImage: item.headImage || "",
            useDefaultIcon: item.useDefaultIcon ?? true,
            role: item.role || "",
          },
        ]);
      }
    });
  }

  /**
   * 失焦校正原文
   * @param blurPara 失焦的段落信息
   */
  public static correctRecordOnBlur(blurPara: { pi: string; pContent: string }) {
    const blurPi = blurPara.pi;
    const focusedPara = collaborationStore.getState().getFocusedPara();
    if (!focusedPara) return;

    const hasEdited = !this._isEqualContent(focusedPara, blurPara.pContent);
    const editedPiArr = collaborationStore.getState().getEditedPiArr();
    collaborationStore.getState().setFocusedPara(null);

    // 只要有一次编辑过，就会永远存在编辑列表中
    if (editedPiArr.includes(blurPi)) {
      console.log(`AsrHandler: 段落 ${blurPi} 已在编辑列表中，跳过校正`);
      return;
    }

    // 此次失焦编辑过，且未添加到已编辑列表中，此时添加到编辑列表
    if (hasEdited) {
      console.log(`AsrHandler: 段落 ${blurPi} 被编辑过，添加到编辑列表`);
      collaborationStore.getState().addEditedPiArr(blurPi);
      return;
    }

    // 从未编辑过，且还未校正，此时校正数据
    const waitingCorrectRecords = collaborationStore.getState().getWaitingCorrectRecords();
    const correctItem = waitingCorrectRecords.find(
      (item) => item.pi === blurPi
    );
    if (!correctItem) {
      console.log(`AsrHandler: 段落 ${blurPi} 没有等待校正的记录`);
      return;
    }

    console.log(`AsrHandler: 段落 ${blurPi} 失焦后执行校正`);
    this._applyCorrectRecord(correctItem);

    // 从等待列表中移除已校正的记录
    const newWaitingRecords = waitingCorrectRecords.filter(item => item.pi !== blurPi);
    collaborationStore.getState().setWaitingCorrectRecords(newWaitingRecords);
  }

  /**
   * 比较段落内容是否相等
   * @param para 段落对象
   * @param content 内容字符串
   * @returns 是否相等
   */
  private static _isEqualContent(para: IPG, content: string): boolean {
    const originalContent = para.sc.map(sItem => sItem.tc).join("");
    const isEqual = originalContent === content;

    return isEqual;
  }
}

export default AsrHandler;
